import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { generateShoppingList } from '../lib/listGenerator'
import { useListStore } from '../stores/listStore'
import { usePreferences } from '../hooks/usePreferences'
import { generateGuestToken, buildGuestUrl } from '../lib/guestToken'
import { type SaleData } from '../components/list/ListItem'
import QuickAddSheet from '../components/list/QuickAddSheet'
import ShopComparison from '../components/list/ShopComparison'
import { ShoppingListMasthead } from '../components/list/ShoppingListMasthead'
import { AisleHeader } from '../components/list/AisleHeader'
import { ShoppingListItemRow } from '../components/list/ShoppingListItemRow'
import { ShoppingListItemDetailSheet } from '../components/list/ShoppingListItemDetailSheet'
import { AddCustomItem } from '../components/list/AddCustomItem'
import { ListActionFooter } from '../components/list/ListActionFooter'
import { type Aisle, groupByAisle } from '../lib/aisleGrouping'
import { shareList } from '../lib/shareList'
import { showToast as showGlobalToast } from '../lib/toastStore'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShoppingList {
  id:           string
  household_id: string
  generated_at: string
}

// row.id          = list_item id  (used for toggle, remove, checked state)
// pantry_items.id = pantry_item id (used for display + finish-shopping payload)
interface RawListItem {
  id:            string
  status:        string
  urgency_score: number
  sort_order:    number
  is_one_off:    boolean
  notes:         string | null
  pantry_items:  { id: string; name: string; emoji: string; display_category: string; location: string | null } | null
}

interface FlatItem {
  id:               string   // list_item id
  item_id:          string   // pantry_item id
  status:           string
  urgency_score:    number
  sort_order:       number
  is_one_off:       boolean
  notes:            string | null
  name:             string
  emoji:            string
  display_category: string
  location:         string | null
}

interface PantryItemForGen {
  id:                     string
  name:                   string
  emoji:                  string
  display_category:       string
  avg_restock_cycle_days: number | null
  last_confirmed_at:      string | null
  confirmation_count:     number
  confidence_score:       number
  is_priority:            boolean
  is_one_off:             boolean
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function flatten(raw: RawListItem[]): FlatItem[] {
  return raw.map(row => ({
    id:               row.id,
    item_id:          row.pantry_items?.id ?? '',
    status:           row.status,
    urgency_score:    row.urgency_score,
    sort_order:       row.sort_order,
    is_one_off:       row.is_one_off,
    notes:            row.notes ?? null,
    name:             row.pantry_items?.name             ?? 'Unknown',
    emoji:            row.pantry_items?.emoji            ?? '📦',
    display_category: row.pantry_items?.display_category ?? 'Other',
    location:         row.pantry_items?.location         ?? null,
  }))
}

// Items with urgency_score >= 90 are "running out" (red in the pattern engine).
// We surface them with a cream-warm tint + terracotta border so they stand out in-store.
const LOW_STOCK_URGENCY_THRESHOLD = 90

type Section = { aisle: Aisle | null; items: FlatItem[] }

function buildSections(
  items: FlatItem[],
  checkedItems: Set<string>,
  sorting: string,
): Section[] {
  // Alphabetical: flat single section, no aisle header
  if (sorting === 'alphabetical') {
    const sorted = [...items].sort((a, b) => {
      const ac = checkedItems.has(a.id)
      const bc = checkedItems.has(b.id)
      if (ac !== bc) return ac ? 1 : -1
      return a.name.localeCompare(b.name)
    })
    return [{ aisle: null, items: sorted }]
  }

  // Group by inferred aisle; within each, push checked to bottom then sort
  const grouped = groupByAisle(items)
  return grouped.map(g => ({
    aisle: g.aisle,
    items: g.items.slice().sort((a, b) => {
      const ac = checkedItems.has(a.id)
      const bc = checkedItems.has(b.id)
      if (ac !== bc) return ac ? 1 : -1
      if (sorting === 'category') return a.name.localeCompare(b.name)
      return a.sort_order - b.sort_order
    }),
  }))
}

// ─── List ─────────────────────────────────────────────────────────────────────

export default function List() {
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const { user }    = useAuth()

  useEffect(() => { document.title = 'Shopping List — Pantry' }, [])

  const { isShoppingMode, setShoppingMode } = useListStore()
  const { listSorting, isPremium } = usePreferences()

  const [checkedItems,     setCheckedItems]    = useState<Set<string>>(new Set())
  const [localRemovedIds,  setLocalRemovedIds] = useState<Set<string>>(new Set())
  const [isGenerating,     setIsGenerating]    = useState(false)
  const [showQuickAdd,     setShowQuickAdd]    = useState(false)
  const [showFinishModal,  setShowFinishModal] = useState(false)
  const [isFinishing,      setIsFinishing]     = useState(false)
  const [toast,            setToast]           = useState<string | null>(null)
  // saleDataMap is kept (empty after the list UI rebuild) so the Unpack navigation
  // state shape stays stable for downstream consumers.
  const [saleDataMap] = useState<Record<string, SaleData>>({})
  const [showComparison,     setShowComparison]    = useState(false)
  const [comparisonPurchIds, setComparisonPurchIds] = useState<string[]>([])
  const [detailItem,         setDetailItem]        = useState<FlatItem | null>(null)
  const [swipedOpenId,       setSwipedOpenId]      = useState<string | null>(null)

  // ── Data fetching ─────────────────────────────────────────────────────────

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return data.household_id as string
    },
  })

  // Realtime sync: shopping list + items. Either window adding/checking
  // items propagates inside ~1s for the other window. `list_items`
  // doesn't have a household_id column (it joins via list_id), so we
  // pass filterColumn: null and rely on RLS to scope per-household.
  useHouseholdRealtime(householdId, {
    tables: [
      'shopping_lists',
      { name: 'list_items', filterColumn: null },
    ],
    invalidateMap: {
      shopping_lists: [['latestList', householdId], ['latestListMeta', householdId], ['active-list', householdId]],
      list_items:     [['listItems'], ['list-items'], ['active-list', householdId], ['latestList', householdId]],
    },
  })

  const { data: activeList, isLoading: listLoading } = useQuery({
    queryKey: ['latestList', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      // Only load lists that haven't been finalized via useFinishUnpack.
      // Without this filter, a previously completed list gets loaded as the
      // active shopping target — the user "shops" on a closed list and Unpack
      // (which filters completed_at IS NULL) finds nothing afterwards.
      const { data, error } = await supabase
        .from('shopping_lists')
        .select('id, household_id, generated_at')
        .eq('household_id', householdId!)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as ShoppingList | null
    },
  })

  const { data: rawItems, isLoading: itemsLoading } = useQuery({
    queryKey: ['listItems', activeList?.id],
    enabled:  !!activeList?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('list_items')
        .select(`
          id, status, urgency_score, sort_order, is_one_off, notes,
          pantry_items ( id, name, emoji, display_category, location )
        `)
        .eq('list_id', activeList!.id)
        .order('sort_order')
      if (error) throw error
      return flatten(data as unknown as RawListItem[])
    },
  })

  const flatItems = rawItems ?? []
  const isLoading = listLoading || (!!activeList && itemsLoading)

  // ── Store price data ───────────────────────────────────────────────────────

  const { data: storePriceData } = useQuery({
    queryKey: ['list-store-prices', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('store_prices')
        .select('item_id, store_name, price, recorded_at')
        .eq('household_id', householdId!)
        .order('recorded_at', { ascending: false })
      return data ?? []
    },
  })

  // ── Active shoppers (household members currently shopping) ───────────────

  const { data: activeShoppers = [] } = useQuery({
    queryKey: ['shopping-sessions', activeList?.id],
    enabled:  !!activeList?.id && isShoppingMode,
    refetchInterval: 15000,
    queryFn:  async () => {
      const cutoff = new Date(Date.now() - 5 * 60 * 1000).toISOString()
      const { data } = await supabase
        .from('shopping_sessions')
        .select('user_id, display_name, last_seen')
        .eq('list_id', activeList!.id)
        .gt('last_seen', cutoff)
      return data ?? []
    },
  })

  // ── Checked-by map (who checked each item) ────────────────────────────────

  const { data: checkedByMap = {} } = useQuery({
    queryKey: ['list-checked-by', activeList?.id],
    enabled:  !!activeList?.id,
    queryFn:  async () => {
      const { data } = await supabase
        .from('list_items')
        .select('id, checked_by')
        .eq('list_id', activeList!.id)
        .not('checked_by', 'is', null)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.checked_by) map[row.id] = 'Someone'
      }
      return map
    },
  })

  // ── Active guest sessions ─────────────────────────────────────────────────

  const { data: activeSessions = [] } = useQuery({
    queryKey: ['guest-sessions', activeList?.id],
    queryFn:  async () => {
      if (!activeList) return []
      const { data } = await supabase
        .from('guest_sessions')
        .select('*')
        .eq('list_id', activeList.id)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
      return data ?? []
    },
    enabled:        !!activeList?.id,
    refetchInterval: 30000,
  })

  // ── Price map ─────────────────────────────────────────────────────────────

  const priceMap = useMemo(() => {
    if (!storePriceData) return {} as Record<string, {
      cheapestStore: string; cheapestPrice: number; storeCount: number;
      avgPrice: number; allStores: { name: string; price: number }[]
    }>

    const byItem: Record<string, { store_name: string; price: number }[]> = {}
    for (const r of storePriceData) {
      if (!byItem[r.item_id]) byItem[r.item_id] = []
      byItem[r.item_id].push({ store_name: r.store_name, price: r.price as number })
    }

    const map: Record<string, {
      cheapestStore: string; cheapestPrice: number; storeCount: number;
      avgPrice: number; allStores: { name: string; price: number }[]
    }> = {}

    for (const [itemId, records] of Object.entries(byItem)) {
      const byStore: Record<string, number[]> = {}
      for (const r of records) {
        if (!byStore[r.store_name]) byStore[r.store_name] = []
        byStore[r.store_name].push(r.price)
      }
      const storeAvgs = Object.entries(byStore)
        .map(([name, prices]) => ({ name, price: prices.reduce((s, p) => s + p, 0) / prices.length }))
        .sort((a, b) => a.price - b.price)
      if (storeAvgs.length === 0) continue
      const allPrices = storeAvgs.map(s => s.price)
      const avgPrice  = allPrices.reduce((s, p) => s + p, 0) / allPrices.length
      map[itemId] = {
        cheapestStore: storeAvgs[0].name,
        cheapestPrice: storeAvgs[0].price,
        storeCount:    storeAvgs.length,
        avgPrice,
        allStores:     storeAvgs,
      }
    }
    return map
  }, [storePriceData])

  // Initialise checked state from DB when data loads
  useEffect(() => {
    if (rawItems) {
      setCheckedItems(new Set(rawItems.filter(i => i.status === 'checked').map(i => i.id)))
    }
  }, [rawItems])

  // Reset local removed ids when a new list loads
  useEffect(() => {
    setLocalRemovedIds(new Set())
  }, [activeList?.id])

  // ── Real-time sync — reflect guest check-offs instantly ───────────────────

  useEffect(() => {
    if (!activeList) return

    const channel = supabase
      .channel(`list-${activeList.id}`)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'list_items',
          filter: `list_id=eq.${activeList.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['listItems', activeList.id] })
        }
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [activeList?.id, queryClient])

  // ── Session heartbeat — broadcast presence while shopping ─────────────────

  useEffect(() => {
    if (!activeList?.id || !user?.id || !isShoppingMode) return

    const upsertSession = async () => {
      try {
        await supabase.from('shopping_sessions').upsert({
          list_id:      activeList.id,
          user_id:      user.id,
          display_name: user.user_metadata?.display_name ?? user.email ?? 'Someone',
          last_seen:    new Date().toISOString(),
        }, { onConflict: 'list_id,user_id' })
      } catch (err) {
        console.error('Session upsert failed:', err)
      }
    }

    upsertSession()
    const interval = setInterval(upsertSession, 30000)

    return () => {
      clearInterval(interval)
      supabase.from('shopping_sessions')
        .delete()
        .eq('list_id', activeList.id)
        .eq('user_id', user.id)
    }
  }, [activeList?.id, user?.id, isShoppingMode])

  // ── Derived ───────────────────────────────────────────────────────────────

  const visibleItems = flatItems.filter(
    i => i.status !== 'removed' && !localRemovedIds.has(i.id)
  )
  const sections     = buildSections(visibleItems, checkedItems, listSorting)
  const checkedCount = visibleItems.filter(i => checkedItems.has(i.id)).length

  const avgTotal       = visibleItems.reduce((s, i) => s + (priceMap[i.item_id]?.avgPrice      || 0), 0)
  const cheapestTotal  = visibleItems.reduce((s, i) => s + (priceMap[i.item_id]?.cheapestPrice || 0), 0)
  const itemsWithPrice = visibleItems.filter(i => !!priceMap[i.item_id]).length
  const itemsWithNoPrice = visibleItems.length - itemsWithPrice

  // ── Helpers ───────────────────────────────────────────────────────────────

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  // ── Toggle ────────────────────────────────────────────────────────────────

  const handleToggle = async (listItemId: string) => {
    setCheckedItems(prev => {
      const next = new Set(prev)
      next.has(listItemId) ? next.delete(listItemId) : next.add(listItemId)
      return next
    })

    const isNowChecked = !checkedItems.has(listItemId)
    const newStatus    = isNowChecked ? 'checked' : 'pending'

    const { error } = await supabase
      .from('list_items')
      .update({
        status:     newStatus,
        checked_by: isNowChecked ? (user?.id ?? null) : null,
        checked_at: isNowChecked ? new Date().toISOString() : null,
      })
      .eq('id', listItemId)

    if (error) console.error('Toggle failed:', error)
  }

  // ── Remove an item (soft-delete via status='removed') + undo toast ────────

  async function handleRemoveItem(item: FlatItem) {
    // Capture the status we're replacing so undo can restore it exactly.
    // If it was 'removed' already (shouldn't normally happen), fall back to 'pending'.
    const prevStatus = item.status === 'removed' ? 'pending' : item.status

    // Close the sheet and any open swipe immediately; optimistic hide
    setDetailItem(null)
    setSwipedOpenId(null)
    setLocalRemovedIds(prev => new Set([...prev, item.id]))

    const { error } = await supabase
      .from('list_items')
      .update({ status: 'removed' })
      .eq('id', item.id)

    if (error) {
      console.error('Remove failed:', error)
      setLocalRemovedIds(prev => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
      showGlobalToast(`Could not remove ${item.name}`, 'error')
      return
    }

    showGlobalToast(`Removed ${item.name}`, {
      durationMs: 5000,
      action: {
        label: 'Undo',
        onClick: async () => {
          // Optimistic un-hide
          setLocalRemovedIds(prev => {
            const next = new Set(prev)
            next.delete(item.id)
            return next
          })
          const { error: restoreErr } = await supabase
            .from('list_items')
            .update({ status: prevStatus })
            .eq('id', item.id)
          if (restoreErr) {
            console.error('Undo remove failed:', restoreErr)
            showGlobalToast(`Could not restore ${item.name}`, 'error')
            return
          }
          queryClient.invalidateQueries({ queryKey: ['listItems', activeList?.id] })
        },
      },
    })
  }

  // ── Finish shopping ───────────────────────────────────────────────────────
  //
  // Important: do NOT write completed_at here. The list stays "active"
  // (completed_at IS NULL) through the entire unpack flow so that
  // useUnpackList — which filters .is('completed_at', null) — can find it.
  // useFinishUnpack writes completed_at as the final step of unpack confirmation.

  async function completeAndNavigate(purchasedIds: string[]) {
    if (!activeList) return
    setIsFinishing(true)

    setShoppingMode(false)
    setIsFinishing(false)
    setShowFinishModal(false)
    localStorage.setItem('unpack_sale_data', JSON.stringify(saleDataMap))
    localStorage.setItem('unpack_purchased_ids', JSON.stringify(purchasedIds))
    localStorage.setItem('unpack_purchased_ids_backup', JSON.stringify(purchasedIds))
    navigate('/unpack', { state: { fromShopping: true, purchasedItemIds: purchasedIds } })
  }

  const handleFinishShopping = async () => {
    if (!activeList) return

    const purchasedIds = flatItems
      .filter(i => checkedItems.has(i.id))
      .map(i => i.item_id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)

    if (isPremium) {
      setComparisonPurchIds(purchasedIds)
      setShowComparison(true)
      setShowFinishModal(false)
      return
    }

    await completeAndNavigate(purchasedIds)
  }

  // ── Generate ──────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      if (!householdId) return

      const { data: pantryItems, error: itemsError } = await supabase
        .from('pantry_items')
        .select('*')
        .eq('household_id', householdId)
        .eq('is_active', true)
      if (itemsError) throw itemsError

      const candidates = generateShoppingList((pantryItems ?? []) as PantryItemForGen[])
      if (candidates.length === 0) return

      const { data: newList, error: listError } = await supabase
        .from('shopping_lists')
        .insert({ household_id: householdId })
        .select('id')
        .single()
      if (listError) throw listError

      const listItems = candidates.map((candidate, index) => ({
        list_id:       newList.id,
        item_id:       candidate.item_id,
        status:        'pending',
        urgency_score: candidate.urgencyScore,
        sort_order:    index,
        is_one_off:    candidate.isOneOff ?? false,
      }))

      const { error: insertError } = await supabase
        .from('list_items')
        .insert(listItems)
      if (insertError) throw insertError

      await queryClient.invalidateQueries({ queryKey: ['latestList'] })
      showToast('List generated ✓')
    } catch (err) {
      console.error('[List] generate failed:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  // ── Guest session ─────────────────────────────────────────────────────────

  const handleCreateGuestSession = async () => {
    if (!activeList || !householdId) return

    try {
      const token     = generateGuestToken()
      const expiresAt = new Date()
      expiresAt.setHours(expiresAt.getHours() + 24)

      const { data, error } = await supabase
        .from('guest_sessions')
        .insert({
          household_id: householdId,
          list_id:      activeList.id,
          token,
          created_by:   user?.id,
          expires_at:   expiresAt.toISOString(),
        })
        .select()
        .single()

      if (error) throw error

      const guestUrl = buildGuestUrl(data.token)

      if (navigator.share) {
        await navigator.share({
          title: 'Pantry Shopping List',
          text:  'Check off items as you shop',
          url:   guestUrl,
        })
      } else {
        await navigator.clipboard.writeText(guestUrl)
        showToast('Guest link copied — valid for 24 hours')
      }

      queryClient.invalidateQueries({ queryKey: ['guest-sessions', activeList.id] })
    } catch (err) {
      console.error('Guest session error:', err)
      showToast('Could not create guest link')
    }
  }

  const handleRevokeSession = async () => {
    if (!activeList) return

    await supabase
      .from('guest_sessions')
      .delete()
      .eq('list_id', activeList.id)

    queryClient.invalidateQueries({ queryKey: ['guest-sessions', activeList.id] })
    showToast('Guest access revoked')
  }

  // ── Share (aisle-grouped text via Web Share API / clipboard) ───────────────

  async function handleShareText() {
    const unchecked = visibleItems.filter(i => !checkedItems.has(i.id))
    const grouped   = groupByAisle(
      unchecked.map(i => ({ name: i.name, display_category: i.display_category, qtyLabel: null })),
    )
    const result = await shareList(grouped)
    if (result.method === 'clipboard' && result.ok) showToast('List copied to clipboard')
  }

  // ── Send to shopper (routes to guest-session flow for premium, paywall otherwise) ─

  function handleSendToShopper() {
    if (isPremium) handleCreateGuestSession()
    else           navigate('/paywall')
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="theme-background min-h-screen flex items-center justify-center">
        <div className="w-9 h-9 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
      </div>
    )
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!activeList) {
    return (
      <div className="theme-background min-h-screen flex flex-col items-center justify-center px-8 gap-5">
        <span className="text-6xl">📋</span>
        <div className="text-center">
          <p className="text-lg font-semibold text-[#2C2A26]">No list yet</p>
          <p className="text-sm text-[#9A9491] mt-1 leading-snug">
            Generate your first list based on what you are running low on
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-white
                     font-semibold text-sm disabled:opacity-60"
          style={{ backgroundColor: 'var(--color-accent)' }}
        >
          {isGenerating && (
            <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          )}
          {isGenerating ? 'Generating…' : 'Generate my list'}
        </button>
      </div>
    )
  }

  // ── Active list ───────────────────────────────────────────────────────────

  return (
    <div className="theme-background min-h-screen pb-32">

      {/* Shopping mode banner */}
      <AnimatePresence>
        {isShoppingMode && (
          <motion.div
            initial={{ y: -64, opacity: 0 }}
            animate={{ y: 0,   opacity: 1 }}
            exit={{    y: -64, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="fixed top-0 left-0 right-0 z-50 text-white
                       flex items-center justify-between px-4 py-3 shadow-md"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            <div className="flex items-center gap-2">
              <span className="text-base">🛒</span>
              <p className="font-semibold text-sm">Shopping mode</p>
            </div>
            <button
              onClick={() => setShowFinishModal(true)}
              className="text-xs font-semibold bg-white text-[#6B9E78] px-3 py-1.5
                         rounded-lg hover:bg-[#f0faf2] transition-colors"
            >
              Done shopping
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active shoppers banner — shown when others are shopping too */}
      <AnimatePresence>
        {isShoppingMode && activeShoppers.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-[52px] left-1/2 -translate-x-1/2 w-full max-w-[480px]
                       z-40 bg-[#E8F2EA] text-[#6B9E78] text-xs font-medium
                       flex items-center gap-1.5 px-4 py-1.5"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-[#6B9E78] animate-pulse shrink-0" />
            {activeShoppers.map(s => s.display_name).join(', ')} shopping now
          </motion.div>
        )}
      </AnimatePresence>

      {/* Editorial masthead */}
      <div
        className="px-4 max-w-[480px] mx-auto transition-all"
        style={{ paddingTop: isShoppingMode ? 72 : 32 }}
      >
        <ShoppingListMasthead
          itemCount={visibleItems.length}
          generatedAt={activeList?.generated_at ?? new Date().toISOString()}
          onBack={() => navigate('/shop')}
          onRegenerate={handleGenerate}
          onShare={handleShareText}
          regenerating={isGenerating}
        />

        {/* Guest session active pill — shown below masthead */}
        {activeList && activeSessions.length > 0 && (
          <div className="mb-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E0EBE2] text-[11px] text-forest w-fit">
            <div className="w-1.5 h-1.5 rounded-full bg-forest animate-pulse" />
            Guest link active
            <button
              onClick={handleRevokeSession}
              className="text-[11px] text-terracotta ml-1 hover:underline font-medium"
            >
              Revoke
            </button>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="max-w-[480px] mx-auto px-4">
        {sections.map(({ aisle, items }) => (
          <section key={aisle ?? '__flat__'} className="mb-5">
            {aisle && <AisleHeader aisle={aisle} count={items.length} />}
            <div className="overflow-hidden rounded-[16px] border border-border-sand bg-paper">
              {items.map((item, idx) => (
                <ShoppingListItemRow
                  key={item.id}
                  id={item.id}
                  name={item.name}
                  qtyLabel={null}
                  isLowStock={item.urgency_score >= LOW_STOCK_URGENCY_THRESHOLD}
                  checked={checkedItems.has(item.id)}
                  isLast={idx === items.length - 1}
                  checkedByName={checkedByMap[item.id] ?? null}
                  notes={item.notes}
                  onToggle={handleToggle}
                  onOpenDetail={() => setDetailItem(item)}
                  onRemove={() => handleRemoveItem(item)}
                  swipedOpen={swipedOpenId === item.id}
                  onSwipeChange={(open) => setSwipedOpenId(open ? item.id : null)}
                />
              ))}
            </div>
          </section>
        ))}

        {/* Add custom item — always visible */}
        {householdId && activeList && (
          <AddCustomItem onOpen={() => setShowQuickAdd(true)} />
        )}

        {/* Estimated cost card — only when at least one item has price data */}
        {itemsWithPrice > 0 && (
          <div className="mx-4 mt-4 mb-2 bg-white rounded-2xl border border-[#E8E2DC] p-4">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              Estimated cost
            </p>

            <div className="flex justify-between mb-2">
              <span className="text-sm text-[#7A7468]">Average prices</span>
              <span className="text-sm font-semibold text-[#2C2A26]">
                ${avgTotal.toFixed(2)}
              </span>
            </div>

            {cheapestTotal < avgTotal && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-[#6B9E78]">Best prices available</span>
                  <span className="text-sm font-semibold text-[#6B9E78]">
                    ${cheapestTotal.toFixed(2)}
                  </span>
                </div>

                <div className="bg-[#E8F2EA] rounded-xl p-3 mt-1">
                  <p className="text-xs text-[#6B9E78] font-medium">
                    💰 Save up to ${(avgTotal - cheapestTotal).toFixed(2)} by choosing the best store per item
                  </p>
                </div>
              </>
            )}

            {itemsWithNoPrice > 0 && (
              <p className="text-xs text-[#B0A99F] mt-2">
                {itemsWithNoPrice} item{itemsWithNoPrice !== 1 ? 's' : ''} have no price data
              </p>
            )}
          </div>
        )}

        {/* Sticky action footer */}
        <ListActionFooter
          mode={isShoppingMode ? 'shopping' : 'browse'}
          itemCount={visibleItems.length}
          onStartShopping={() => setShoppingMode(true)}
          onFinishShopping={() => setShowFinishModal(true)}
          onRegenerate={handleGenerate}
          onShare={handleSendToShopper}
          regenerating={isGenerating}
        />
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{    opacity: 0, y: 12 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 whitespace-nowrap
                       bg-[#2C2A26] text-white text-xs font-medium
                       px-4 py-2.5 rounded-xl shadow-lg z-50"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Add Sheet */}
      {showQuickAdd && householdId && activeList && (
        <QuickAddSheet
          householdId={householdId}
          listId={activeList.id}
          onClose={() => setShowQuickAdd(false)}
          onAdded={name => showToast(`${name} added`)}
        />
      )}

      {/* Item detail sheet — opened by tapping the item name on a row */}
      <AnimatePresence>
        {detailItem && (
          <ShoppingListItemDetailSheet
            id={detailItem.id}
            name={detailItem.name}
            isLowStock={detailItem.urgency_score >= LOW_STOCK_URGENCY_THRESHOLD}
            notes={detailItem.notes}
            onClose={() => setDetailItem(null)}
            onRemove={() => handleRemoveItem(detailItem)}
          />
        )}
      </AnimatePresence>

      {/* Shop comparison — Pantry + */}
      {showComparison && (
        <ShopComparison
          listItems={flatItems}
          purchasedIds={comparisonPurchIds}
          onContinue={() => {
            setShowComparison(false)
            completeAndNavigate(comparisonPurchIds)
          }}
          onSkip={() => {
            setShowComparison(false)
            completeAndNavigate(comparisonPurchIds)
          }}
        />
      )}

      {/* Finish shopping modal */}
      <AnimatePresence>
        {showFinishModal && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-50 bg-black/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFinishModal(false)}
            />

            {/* Sheet */}
            <motion.div
              className="theme-background fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl
                         shadow-2xl px-6 pt-5 pb-10 max-w-[480px] mx-auto"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            >
              {/* Handle */}
              <div className="flex justify-center mb-5">
                <div className="w-10 h-1 rounded-full theme-border" style={{ backgroundColor: 'var(--color-border)' }} />
              </div>

              <h2 className="text-lg font-semibold theme-text-primary text-center">
                Done shopping?
              </h2>
              <p className="text-sm text-[#9A9491] text-center mt-2 mb-6">
                {checkedCount > 0
                  ? `You checked off ${checkedCount} item${checkedCount !== 1 ? 's' : ''}. Update your pantry now?`
                  : 'Update your pantry with what you bought.'}
              </p>

              <button
                onClick={handleFinishShopping}
                disabled={isFinishing}
                className="w-full py-3.5 rounded-2xl text-white font-semibold
                           text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--color-accent)' }}
              >
                {isFinishing && (
                  <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                )}
                {isFinishing ? 'Saving…' : 'Update my pantry'}
              </button>

              <button
                onClick={() => setShowFinishModal(false)}
                className="w-full mt-3 py-3 text-sm font-medium text-[#7A7468]
                           hover:text-[#2C2A26] transition-colors"
              >
                Not yet
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  )
}
